import { Outlet, Route, Routes, useLocation } from 'react-router-dom';
import { CommandPalette } from './components/CommandPalette.js';
import { ErrorBoundary } from './components/ErrorBoundary.js';
import { GlobalProgressBar } from './components/GlobalProgressBar.js';
import { IframeShell } from './components/layout/IframeShell.js';
import { LoadingScreen } from './components/layout/LoadingScreen.js';
import { RequireAuth } from './components/RequireAuth.js';
import { HomePopupProvider } from './features/common/home-popup-context.js';
import { LicenseProvider, useLicense } from './features/common/license-context.js';
import { DemoModeProvider } from './features/demo/index.js';
import { ThemeProvider } from './lib/theme-context.js';
import { AccessDeniedPage } from './features/common/pages/AccessDeniedPage.js';
import { HomePage, LandingPage } from './features/common/pages/HomePage.js';
import { LicenseExpiredPage } from './features/common/pages/LicenseExpiredPage.js';
import { NotFoundPage } from './features/common/pages/NotFoundPage.js';
import { dashboardRoutes } from './features/dashboard/routes.js';
import { kpiRoutes } from './features/kpi/routes.js';
import { planningRoutes } from './features/planning/routes.js';
import { presentationRoutes } from './features/presentation/routes.js';
import { scenarioRoutes } from './features/scenario/routes.js';
import { setupRoutes } from './features/setup/routes.js';
import { supportRoutes } from './features/support/routes.js';
import { userRoutes } from './features/user/routes.js';
import { valueRoutes } from './features/value/routes.js';
import { workfrontRoutes } from './features/workfront/routes.js';

/**
 * Top-level route tree for the customer SPA. The common slice supplies
 * the `<IframeShell>` layout (with logo + version footer) and the
 * `<ErrorBoundary>` fallback. Every other slice ships its own
 * `routes.tsx` returning a fragment of `<Route>` children, composed
 * here under a single `<RequireAuth>` gate so the auth boundary is
 * obvious at the top of the file.
 *
 * Slice routes are placeholders today — the layout + navigation are
 * scaffolded so per-slice implementations land independently without
 * touching this file.
 */
export function App(): JSX.Element {
  return (
    <ErrorBoundary>
      <ThemeProvider>
        <GlobalProgressBar />
        <Routes>
          {/* Auth + license + demo-mode providers wrap everything so
              the same context (and React-Query cache) is shared across
              the chrome-less landing page and the chromed slice
              routes. The outer element renders <Outlet/> via
              `AuthLayout` so nested routes can pick whether they want
              the IframeShell. */}
          <Route element={<AuthLayout />}>
            {/* `/` is the chrome-less landing page — the welcome
                screen the operator sees on a fresh visit. No sidebar,
                no top bar. Operator-reported: "initial page should
                look like current home page but without left menu and
                top header elements, just popup in center". */}
            <Route path="/" element={<LandingPage />} />

            {/* Every other authenticated route renders inside the
                IframeShell. `/home` is the chromed Home view that the
                sidebar's Home menu item points at — same hero
                content as the landing, but with the left rail
                visible. Logo click anywhere raises an overlay popup
                via `HomePopupProvider` without changing routes. */}
            <Route element={<IframeShell />}>
              <Route element={<SliceRoutes />}>
                <Route path="/home" element={<HomePage />} />
                <Route path="/access-denied" element={<AccessDeniedPage />} />
                {dashboardRoutes()}
                {scenarioRoutes()}
                {kpiRoutes()}
                {valueRoutes()}
                {planningRoutes()}
                {workfrontRoutes()}
                {presentationRoutes()}
                {setupRoutes()}
                {userRoutes()}
                {supportRoutes()}
              </Route>
            </Route>
          </Route>
          <Route path="*" element={<NotFoundPage />} />
        </Routes>
      </ThemeProvider>
    </ErrorBoundary>
  );
}

/**
 * Outer layout — owns RequireAuth, LicenseProvider, DemoModeProvider,
 * and the LicenseGate. Renders `<Outlet/>` so the route tree below
 * can decide whether to wrap content in `<IframeShell/>` (chromed
 * slice routes) or render it chrome-less (`/` landing page).
 *
 * Centralising the providers here means the React-Query cache, the
 * license context, and the demo-mode toggle all persist across
 * navigation between the landing page and slice routes without
 * remounting / refetching.
 */
function AuthLayout(): JSX.Element {
  return (
    <RequireAuth>
      <LicenseProvider>
        <DemoModeProvider>
          <HomePopupProvider>
            <LicenseGate>
              <Outlet />
            </LicenseGate>
          </HomePopupProvider>
        </DemoModeProvider>
      </LicenseProvider>
    </RequireAuth>
  );
}

/**
 * Render gate that sits between LicenseProvider and the per-slice
 * routes. While the initial `/v1/license` fetch is pending → full-
 * page LoadingScreen so the chrome doesn't shift. If the license is
 * expired or disabled → swap the slice tree for LicenseExpiredPage.
 * Otherwise pass through.
 */
function LicenseGate({ children }: { children: JSX.Element }): JSX.Element {
  const { details, isLoading, isExpired } = useLicense();
  if (isLoading && !details) return <LoadingScreen />;
  if (isExpired) return <LicenseExpiredPage />;
  return children;
}

/**
 * Layout-only RequireAuth child — renders `<Outlet>` so the per-slice
 * `<Route>` children below can attach to it. React-router needs a
 * pass-through component here because the layout `<Route element>`
 * is the IframeShell.
 */
function SliceRoutes(): JSX.Element {
  const location = useLocation();
  // Re-key the per-route boundary on pathname so navigating away
  // unmounts the failed tree — a user that bumps into a broken
  // page can hop somewhere else via the sidebar / Cmd+K and the
  // SPA recovers without a full reload.
  return (
    <>
      <CommandPalette />
      <ErrorBoundary key={location.pathname} scope={pathnameScope(location.pathname)}>
        <Outlet />
      </ErrorBoundary>
    </>
  );
}

/**
 * Convert `/scenarios/administrator` → `Scenarios` for the boundary's
 * "the X page hit an error" copy. Falls back to "page" for `/` and
 * any unmapped paths.
 */
function pathnameScope(pathname: string): string {
  const first = pathname.split('/').filter(Boolean)[0];
  if (!first) return 'page';
  // Friendly capitalisation; matches the sidebar labels closely
  // enough that the operator recognises the section.
  const friendly: Record<string, string> = {
    home: 'Home',
    dashboard: 'Daily Dashboard',
    scenarios: 'Scenarios',
    kpi: 'KPI Builder',
    value: 'Value',
    planning: 'Planning',
    workfront: 'Workfront',
    presentation: 'Presentation',
    user: 'User Settings',
    setup: 'Setup',
    support: 'Support',
  };
  return friendly[first] ?? first;
}
